<template>
    <div class="lightbox" :class="{'lightbox--visible': visible}" @click="close">
        <transition name="lightbox-backdrop">
            <div class="lightbox-backdrop" v-if="visible"></div>
        </transition>
        <transition name="lightbox-content">
            <div class="lightbox-content" :style="vars" v-if="visible">
                <img :src="currentSrc" alt="">
                <component :src="src" alt="" :is="tag" />
            </div>
        </transition>
    </div>
</template>

<script type="text/babel">
export default {
  data() {
    return {
      visible: false,
      boundingBox: null,
      src: null,
      currentSrc: null,
      windowSize: null,
      tag: 'img',
    };
  },
  methods: {
    getWindowSize() {
      return {
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.9,
      };
    },
    close() {
      this.visible = false;
    },
    open() {
      this.$nextTick(() => {
        this.visible = true;
      });
    },
  },
  computed: {
    aspectRatio() {
      return this.boundingBox.width / this.boundingBox.height;
    },
    windowRatio() {
      return this.windowSize.width / this.windowSize.height;
    },
    portrait() {
      return this.boundingBox.width <= this.boundingBox.height;
    },
    widescreen() {
      return this.windowSize.height <= this.windowSize.width;
    },
    scale() {
      return {
        x: this.boundingBox.width / this.windowSize.width,
        y: this.boundingBox.height / this.windowSize.height,
      };
    },
    vars() {
      let scale;
      let height;
      let width;

      const { left, top } = this.boundingBox;

      if (this.aspectRatio < this.windowRatio) {
        ({ height } = this.windowSize);
        width = height * this.aspectRatio;
        scale = this.scale.y;
      } else {
        ({ width } = this.windowSize);
        height = width / this.aspectRatio;
        scale = this.scale.x;
      }
      return {
        '--scale': scale,
        '--height': `${height}px`,
        '--width': `${width}px`,
        '--top': `${top}px`,
        '--left': `${left}px`,
      };
    },
    style() {
      const style = {};
      const transforms = [];

      transforms.push(`translateX(${this.boundingBox.left}px)`);
      transforms.push(`translateY(${this.boundingBox.top}px)`);
      if (this.portrait || this.widescreen) {
        style.height = `${this.windowSize.height}px`;
        style.width = `${(this.windowSize.height * this.aspectRatio)}px`;
        transforms.push(`scale(${this.scale.y})`);
      } else {
        style.height = `${(this.windowSize.height / this.aspectRatio)}px`;
        style.width = `${this.windowSize.width}px`;
        transforms.push(`scale(${this.scale.x})`);
      }

      style.transform = transforms.join(' ');
      return style;
    },
  },
  created() {
    // eslint-disable-next-line object-curly-newline
    this.$eventHub.$on('lightbox', ({ el, src, currentSrc, iframe }) => {
      this.boundingBox = el.getBoundingClientRect().toJSON();
      this.src = src;
      this.elm = el;
      this.currentSrc = currentSrc;
      this.open();
      this.tag = iframe ? 'iframe' : 'img';
    });

    window.addEventListener('resize', () => {
      this.windowSize = this.getWindowSize();
      if (this.elm) {
        this.boundingBox = this.elm.getBoundingClientRect().toJSON();
      }
    });
    this.windowSize = this.getWindowSize();
  },
};

export const LightboxDirective = {
  bind(el, binding, vnode) {
    el.addEventListener('click', () => {
      vnode.context.$eventHub.$emit('lightbox', {
        el,
        src: binding.value,
        currentSrc: el.src,
        iframe: !!binding.modifiers.iframe,
      });
    });
  },
};
</script>

<style lang="scss">
    .lightbox {
        --duration: 500ms;
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        &--visible {
            pointer-events: all;
        }
        &-backdrop {
            --opacity: .8;
            position: absolute;
            width: 100%;
            height: 100%;
            background: #000;
            will-change: opacity;
            opacity: var(--opacity);
            backdrop-filter: blur(2px);
            &-enter-active, &-leave-active {
                transition: all var(--duration) cubic-bezier(0.4, 0.0, 0.2, 1);
            }
            &-enter, &-leave-to {
                opacity: 0;
                backdrop-filter: blur(0px);
            }
            &-enter-to, &-leave {
                opacity: var(--opacity);
                backdrop-filter: blur(2px);
            }
        }
        &-content {
            background: #fff;
            position: absolute;
            width: var(--width);
            height: var(--height);
            transform-origin: 0 0;
            left: 0;
            top: 0;
            transform: translateX(calc(50vw - 50%)) translateY(calc(50vh - 50%));
            box-shadow: 0 4px 25px rgba(#000, 0.2), 0 4px 10px rgba(#000, 0.1);
            img, iframe {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                border: none;
                pointer-events: none;
            }
            &-enter-active, &-leave-active {
                transition: transform var(--duration) cubic-bezier(0.4, 0.0, 0.2, 1);
            }
            &-enter, &-leave-to {
                transform: translateY(var(--top)) translateX(var(--left)) scale(var(--scale));
            }
            &-enter-to, &-leave {
                transform: translateX(calc(50vw - 50%)) translateY(calc(50vh - 50%))!important;
            }
        }
    }
</style>
